import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type {
  UUID,
  WorkspaceMember,
  WorkspaceMemberAddPayload,
  WorkspaceMemberRoleUpdatePayload,
} from "@/lib/types";

/** Three mutations for workspace member management, all invalidating the
 * workspace members query on success. Keeps the surface tight. */
export function useWorkspaceMemberMutations(workspaceId: UUID | undefined) {
  const qc = useQueryClient();
  const key = workspaceId
    ? queryKeys.workspaceMembers(workspaceId)
    : ["workspaces", "void", "members"];

  const add = useMutation<WorkspaceMember, Error, WorkspaceMemberAddPayload>({
    mutationFn: (payload) => api.addWorkspaceMember(workspaceId as UUID, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  const updateRole = useMutation<
    WorkspaceMember,
    Error,
    { userId: UUID; role: WorkspaceMemberRoleUpdatePayload["role"] }
  >({
    mutationFn: ({ userId, role }) =>
      api.updateWorkspaceMemberRole(workspaceId as UUID, userId, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  const remove = useMutation<void, Error, UUID>({
    mutationFn: (userId) => api.removeWorkspaceMember(workspaceId as UUID, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  return { add, updateRole, remove };
}
